import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Bot, LayoutDashboard, Users, HeartHandshake, Scale, ShieldCheck, LogOut, Radio, DollarSign, MessageSquare, Zap, UserPlus, BrainCircuit, Upload } from "lucide-react";
import { useAuth, api } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const T = {
  en: {
    title: "Super Admin", overview: "Overview", users: "Users", matches: "Matches", legal: "Legal Consents", security: "Security Logs", logout: "Log Out",
    totalUsers: "Total Users", new24: "New (24h)", live: "Live Avatars", matchesTotal: "Total Matches", matches24: "Matches (24h)",
    mutual: "Mutual Connections", messages: "Human Messages", consents: "Consents Signed", mrr: "MRR Estimate", byTier: "Users by Plan",
    colUser: "User", colContact: "Contact", colPlan: "Plan", colRole: "Role", colAvatar: "Avatar", colStatus: "Status", colLegal: "Legal", colCredits: "Credits Used",
    colPair: "Avatars", colCompat: "Compat.", colMatchStatus: "Status", colDate: "Date",
    colVersion: "Version", colIp: "IP", colSignature: "Signature", colEvent: "Event",
    engines: "Simulation Engines", enginesSub: "Force a cycle of each autonomous engine with one click.",
    engForum: "Force Reddit Debate Round", engTribes: "Force Tribe Wall Posts", engSocial: "Force Cyber-Wall Activity",
    engRunning: "Running...", engDone: "Cycle complete:",
    mmTitle: "💘 Matchmaking & Proximity Radar", mmSub: "Master switch. When OFF, the radar, matches and the match-focused Hero copy are hidden from the whole platform. Turn it ON to restore everything instantly (including the original Hero text).",
    mmOn: "ACTIVE — visible to users", mmOff: "DISABLED — hidden from users", mmBtnOn: "Turn Off Matchmaking", mmBtnOff: "Reactivate Matchmaking & Radar",
    intelTab: "Intelligence", intelUploadTitle: "Intelligence Codes — Upload Valid Codes",
    intelUploadSub: "Upload a .txt or .csv file with one code per line. Kids redeem these codes (given by parents or teachers) to raise their creature's INTELLIGENCE level. Each code is SINGLE-USE: once a child redeems it, it burns for everyone.",
    intelUploadBtn: "Upload Codes File", intelTotal: "Total Codes", intelAvailable: "Available", intelRedeemedLbl: "Redeemed",
    intelFiles: "Uploaded Files", intelRecent: "Recent Redemptions", colFile: "File", colCodes: "Codes", colCode: "Code", colAvatarName: "Creature",
    intelUploadDone: "added", intelUploadDup: "duplicates skipped",
    wcTitle: "🏆 World Cup 2026 Thread", wcSub: "Switch the world-feed simulated thread to the current tournament stage.",
    wcStages: { octavos: "Octavos", cuartos: "Cuartos", semis: "Semifinales", final: "LA FINAL" },
  },
  es: {
    title: "Super Admin", overview: "Resumen", users: "Usuarios", matches: "Matches", legal: "Consentimientos", security: "Logs de Seguridad", logout: "Salir",
    totalUsers: "Usuarios Totales", new24: "Nuevos (24h)", live: "Avatares Live", matchesTotal: "Matches Totales", matches24: "Matches (24h)",
    mutual: "Conexiones Mutuas", messages: "Mensajes Humanos", consents: "Consentimientos", mrr: "MRR Estimado", byTier: "Usuarios por Plan",
    colUser: "Usuario", colContact: "Contacto", colPlan: "Plan", colRole: "Rol", colAvatar: "Avatar", colStatus: "Estado", colLegal: "Legal", colCredits: "Créditos Usados",
    colPair: "Avatares", colCompat: "Compat.", colMatchStatus: "Estado", colDate: "Fecha",
    colVersion: "Versión", colIp: "IP", colSignature: "Firma", colEvent: "Evento",
    engines: "Motores de Simulación", enginesSub: "Fuerza un ciclo de cada motor autónomo con un clic.",
    engForum: "Forzar Ronda de Debates Reddit", engTribes: "Forzar Posts de Tribus", engSocial: "Forzar Actividad del Cyber-Muro",
    engRunning: "Ejecutando...", engDone: "Ciclo completado:",
    mmTitle: "💘 Matchmaking y Radar de Proximidad", mmSub: "Interruptor maestro. En OFF, el radar, los matches y la redacción de matches del Hero quedan ocultos en toda la plataforma. Actívalo para restaurar todo al instante (incluyendo el texto original del Hero).",
    mmOn: "ACTIVO — visible para usuarios", mmOff: "DESACTIVADO — oculto para usuarios", mmBtnOn: "Apagar Matchmaking", mmBtnOff: "Reactivar Matchmaking y Radar",
    intelTab: "Inteligencia", intelUploadTitle: "Códigos de Inteligencia — Subir Códigos Válidos",
    intelUploadSub: "Sube un archivo .txt o .csv con un código por línea. Los niños canjean estos códigos (entregados por papás o maestros) para subir el nivel de INTELIGENCIA de su criatura. Cada código es de UN SOLO USO: al canjearlo un niño, se quema para todos.",
    intelUploadBtn: "Subir Archivo de Códigos", intelTotal: "Códigos Totales", intelAvailable: "Disponibles", intelRedeemedLbl: "Canjeados",
    intelFiles: "Archivos Subidos", intelRecent: "Canjes Recientes", colFile: "Archivo", colCodes: "Códigos", colCode: "Código", colAvatarName: "Criatura",
    intelUploadDone: "agregados", intelUploadDup: "duplicados omitidos",
    wcTitle: "🏆 Hilo del Mundial 2026", wcSub: "Cambia el hilo simulado del feed mundial a la etapa actual del torneo.",
    wcStages: { octavos: "Octavos", cuartos: "Cuartos", semis: "Semifinales", final: "LA FINAL" },
  },
};

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-5" data-testid={`sa-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2 flex items-center gap-1.5"><Icon size={12} style={{ color }} /> {label}</p>
    <p className="font-heading text-2xl font-black" style={{ color }}>{value}</p>
  </div>
);

const Table = ({ cols, rows, testId }) => (
  <div className="overflow-x-auto bg-[#0F0F1A]/80 border border-white/10 rounded-3xl" data-testid={testId}>
    <table className="w-full text-left">
      <thead>
        <tr className="border-b border-white/10">
          {cols.map((c) => <th key={c} className="px-4 py-3 text-[9px] font-bold uppercase tracking-[0.2em] text-white/40 whitespace-nowrap">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]">
            {r.map((cell, j) => <td key={j} className="px-4 py-3 text-xs text-white/70 whitespace-nowrap max-w-[220px] truncate">{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length === 0 && <p className="text-xs text-white/30 p-5">—</p>}
  </div>
);

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const { lang, setLang } = useLang();
  const t = T[lang] || T.en;
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ users: [], matches: [], consents: [], logs: [] });
  const [engineBusy, setEngineBusy] = useState(null);
  const [engineResult, setEngineResult] = useState(null);
  const [features, setFeatures] = useState(null);
  const [mmBusy, setMmBusy] = useState(false);
  const [intel, setIntel] = useState(null);
  const [intelBusy, setIntelBusy] = useState(false);
  const [intelMsg, setIntelMsg] = useState(null);

  const loadIntel = () => api.get("/admin/intelligence/stats").then((r) => setIntel(r.data)).catch(() => {});

  const uploadCodes = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setIntelBusy(true);
    setIntelMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: res } = await api.post("/admin/intelligence/upload", fd);
      setIntelMsg(`✅ ${res.file_name}: ${res.added} ${t.intelUploadDone} · ${res.duplicates_skipped} ${t.intelUploadDup}`);
      loadIntel();
    } catch (err) {
      setIntelMsg(`❌ ${err.response?.data?.detail || "Error"}`);
    } finally {
      setIntelBusy(false);
      e.target.value = "";
    }
  };

  const toggleMatchmaking = async () => {
    setMmBusy(true);
    try {
      const { data: res } = await api.post("/admin/features", { matchmaking_enabled: !features?.matchmaking_enabled });
      setFeatures(res);
    } catch {} finally {
      setMmBusy(false);
    }
  };

  const runWorldCup = async (stage) => {
    setEngineBusy(`wc-${stage}`);
    setEngineResult(null);
    try {
      const { data: res } = await api.post("/admin/worldcup-thread", { stage });
      setEngineResult({ id: `wc-${stage}`, text: `stage: ${res.stage} · posts: ${res.posts} · comments: ${res.comments}` });
    } catch (e) {
      setEngineResult({ id: `wc-${stage}`, text: e.response?.data?.detail || "Error" });
    } finally {
      setEngineBusy(null);
    }
  };

  const runEngine = async (id, endpoint) => {
    setEngineBusy(id);
    setEngineResult(null);
    try {
      const { data: res } = await api.post(endpoint);
      const parts = Object.entries(res).filter(([k]) => k !== "ok").map(([k, v]) => `${k}: ${v}`).join(" · ");
      setEngineResult({ id, text: parts });
    } catch (e) {
      setEngineResult({ id, text: e.response?.data?.detail || "Error" });
    } finally {
      setEngineBusy(null);
    }
  };

  useEffect(() => {
    if (user?.role !== "superadmin") return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/features").then((r) => setFeatures(r.data)).catch(() => {});
    loadIntel();
    Promise.all([api.get("/admin/users"), api.get("/admin/matches"), api.get("/admin/consents"), api.get("/admin/audit-logs")])
      .then(([u, m, c, l]) => setData({ users: u.data, matches: m.data, consents: c.data, logs: l.data }))
      .catch(() => {});
  }, [user]);

  if (user?.role !== "superadmin") return <Navigate to="/dashboard" replace />;

  const tabs = [
    { id: "overview", label: t.overview, icon: LayoutDashboard },
    { id: "intel", label: t.intelTab, icon: BrainCircuit },
    { id: "users", label: t.users, icon: Users },
    { id: "matches", label: t.matches, icon: HeartHandshake },
    { id: "legal", label: t.legal, icon: Scale },
    { id: "security", label: t.security, icon: ShieldCheck },
  ];

  return (
    <div className="min-h-screen bg-[#05050A] text-white flex" data-testid="superadmin-page">
      <aside className="hidden md:flex flex-col w-60 border-r border-[#B026FF]/20 bg-[#0F0F1A]/50 p-5 fixed inset-y-0">
        <Link to="/" className="flex items-center gap-2 font-heading font-black text-sm mb-2">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#B026FF] to-[#00F5FF] flex items-center justify-center"><Bot size={18} /></span>
          AVATAR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">4 U</span>
        </Link>
        <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-[#B026FF] mb-8" data-testid="superadmin-badge">⚡ {t.title}</p>
        <nav className="space-y-1 flex-1">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`sa-nav-${id}`} onClick={() => setTab(id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-colors ${
                tab === id ? "bg-[#B026FF]/15 text-white border border-[#B026FF]/30" : "text-white/50 hover:text-white hover:bg-white/5"
              }`}>
              <Icon size={17} /> {label}
            </button>
          ))}
        </nav>
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 mb-4 self-start">
          {["en", "es"].map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}-btn`}
              className={`text-[10px] font-bold px-3 py-1 rounded-full uppercase ${lang === l ? "bg-[#B026FF] text-white" : "text-white/50"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="sa-logout-btn" onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-white/50 hover:text-red-400 hover:bg-red-500/5 transition-colors">
          <LogOut size={17} /> {t.logout}
        </button>
      </aside>

      <main className="flex-1 md:ml-60 p-6 md:p-10">
        <div className="md:hidden flex items-center gap-2 overflow-x-auto mb-6">
          {tabs.map(({ id, label }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap ${tab === id ? "bg-[#B026FF]/20 border border-[#B026FF]/40" : "bg-white/5 text-white/50"}`}>
              {label}
            </button>
          ))}
        </div>

        <h1 className="font-heading text-2xl md:text-3xl font-black mb-8">{tabs.find((x) => x.id === tab)?.label}</h1>

        {tab === "overview" && stats && (
          <div data-testid="sa-overview">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              <Stat icon={Users} label={t.totalUsers} value={stats.total_users} color="#00F5FF" />
              <Stat icon={UserPlus} label={t.new24} value={stats.new_users_24h} color="#00FFAA" />
              <Stat icon={Radio} label={t.live} value={stats.live_avatars} color="#00FFAA" />
              <Stat icon={DollarSign} label={t.mrr} value={`$${stats.mrr_estimate}`} color="#FFD166" />
              <Stat icon={HeartHandshake} label={t.matchesTotal} value={stats.matches_total} color="#D080FF" />
              <Stat icon={Zap} label={t.matches24} value={stats.matches_24h} color="#D080FF" />
              <Stat icon={HeartHandshake} label={t.mutual} value={stats.mutual_connections} color="#FF6EC7" />
              <Stat icon={MessageSquare} label={t.messages} value={stats.chat_messages} color="#00F5FF" />
            </div>
            <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6" data-testid="sa-tier-breakdown">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-4">{t.byTier} · {t.consents}: {stats.legal_consents}</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(stats.tiers).map(([k, v]) => (
                  <span key={k} className="text-xs font-bold px-4 py-2 rounded-full bg-white/5 border border-white/10 uppercase">
                    {k}: <span className="text-[#00F5FF]">{v}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Matchmaking & Radar master switch */}
            <div className="bg-[#0F0F1A]/80 border border-[#FF6EC7]/25 rounded-3xl p-6 mt-6" data-testid="sa-matchmaking-panel">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF6EC7] font-bold mb-1">{t.mmTitle}</p>
              <p className="text-[11px] text-white/40 mb-4">{t.mmSub}</p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <span
                  data-testid="sa-matchmaking-status"
                  className={`inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full border ${
                    features?.matchmaking_enabled
                      ? "text-[#00FFAA] border-[#00FFAA]/40 bg-[#00FFAA]/10"
                      : "text-white/40 border-white/15 bg-white/5"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${features?.matchmaking_enabled ? "bg-[#00FFAA] animate-pulse" : "bg-white/30"}`} />
                  {features?.matchmaking_enabled ? t.mmOn : t.mmOff}
                </span>
                <button
                  data-testid="sa-matchmaking-toggle"
                  onClick={toggleMatchmaking}
                  disabled={mmBusy || features === null}
                  className={`py-3.5 px-6 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0 ${
                    features?.matchmaking_enabled
                      ? "text-[#FF4444] border-[#FF4444]/50 bg-[#FF4444]/10"
                      : "text-[#FF6EC7] border-[#FF6EC7]/50 bg-[#FF6EC7]/10 shadow-[0_0_14px_rgba(255,110,199,0.25)]"
                  }`}
                >
                  {mmBusy ? t.engRunning : features?.matchmaking_enabled ? t.mmBtnOn : t.mmBtnOff}
                </button>
              </div>
            </div>

            {/* Simulation Engine Control */}
            <div className="bg-[#0F0F1A]/80 border border-[#FFA726]/25 rounded-3xl p-6 mt-6" data-testid="sa-engines-panel">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#FFA726] font-bold mb-1">⚙️ {t.engines}</p>
              <p className="text-[11px] text-white/40 mb-4">{t.enginesSub}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { id: "forum", label: t.engForum, endpoint: "/admin/simulate-forum", color: "#FFA726" },
                  { id: "tribes", label: t.engTribes, endpoint: "/admin/simulate-tribes", color: "#00F5FF" },
                  { id: "social", label: t.engSocial, endpoint: "/admin/simulate-social", color: "#B026FF" },
                ].map(({ id, label, endpoint, color }) => (
                  <button
                    key={id}
                    data-testid={`sa-engine-${id}`}
                    onClick={() => runEngine(id, endpoint)}
                    disabled={engineBusy !== null}
                    className="py-3.5 px-4 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0"
                    style={{ color, borderColor: `${color}50`, backgroundColor: `${color}10`, boxShadow: `0 0 14px ${color}22` }}
                  >
                    {engineBusy === id ? t.engRunning : label}
                  </button>
                ))}
              </div>
              {/* World Cup thread switcher */}
              <div className="mt-6 pt-5 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#00FFAA] font-bold mb-1">{t.wcTitle}</p>
                <p className="text-[11px] text-white/40 mb-4">{t.wcSub}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(t.wcStages).map(([stage, label]) => (
                    <button
                      key={stage}
                      data-testid={`sa-worldcup-${stage}`}
                      onClick={() => runWorldCup(stage)}
                      disabled={engineBusy !== null}
                      className={`py-3.5 px-4 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0 ${
                        stage === "final"
                          ? "text-[#FFD700] border-[#FFD700]/50 bg-[#FFD700]/10 shadow-[0_0_14px_rgba(255,215,0,0.25)]"
                          : "text-[#00FFAA] border-[#00FFAA]/40 bg-[#00FFAA]/8"
                      }`}
                    >
                      {engineBusy === `wc-${stage}` ? t.engRunning : label}
                    </button>
                  ))}
                </div>
              </div>
              {engineResult && (
                <p className="text-[11px] font-bold text-[#00FFAA] mt-4" data-testid="sa-engine-result">
                  ✅ {t.engDone} {engineResult.text}
                </p>
              )}
            </div>
          </div>
        )}

        {tab === "intel" && (
          <div data-testid="sa-intel-section">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <Stat icon={BrainCircuit} label={t.intelTotal} value={intel?.total_codes ?? "—"} color="#00F5FF" />
              <Stat icon={Zap} label={t.intelAvailable} value={intel?.available ?? "—"} color="#00FFAA" />
              <Stat icon={BrainCircuit} label={t.intelRedeemedLbl} value={intel?.redeemed ?? "—"} color="#FF6EC7" />
            </div>

            <div className="bg-[#0F0F1A]/80 border border-[#00F5FF]/25 rounded-3xl p-6 mb-8" data-testid="sa-intel-upload-panel">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#00F5FF] font-bold mb-1">🧠 {t.intelUploadTitle}</p>
              <p className="text-[11px] text-white/40 mb-4">{t.intelUploadSub}</p>
              <label className={`inline-flex items-center gap-2 py-3.5 px-6 rounded-2xl text-[10px] font-black uppercase tracking-widest border cursor-pointer transition-all hover:-translate-y-0.5 text-[#00F5FF] border-[#00F5FF]/50 bg-[#00F5FF]/10 shadow-[0_0_14px_rgba(0,245,255,0.2)] ${intelBusy ? "opacity-50 pointer-events-none" : ""}`}>
                <Upload size={14} />
                {intelBusy ? t.engRunning : t.intelUploadBtn}
                <input type="file" accept=".txt,.csv" className="hidden" onChange={uploadCodes} disabled={intelBusy} data-testid="sa-intel-file-input" />
              </label>
              {intelMsg && <p className="text-[11px] font-bold text-[#00FFAA] mt-4" data-testid="sa-intel-upload-result">{intelMsg}</p>}
            </div>

            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-3">{t.intelFiles}</p>
            <Table testId="sa-intel-files-table"
              cols={[t.colFile, t.colCodes, t.intelRedeemedLbl, t.colDate]}
              rows={(intel?.files || []).map((f) => [f.file_name, f.codes, f.redeemed, fmtDate(f.uploaded_at)])} />

            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-3 mt-8">{t.intelRecent}</p>
            <Table testId="sa-intel-redemptions-table"
              cols={[t.colCode, t.colUser, t.colAvatarName, t.colDate]}
              rows={(intel?.recent_redemptions || []).map((r) => [r.code, r.user, r.avatar, fmtDate(r.redeemed_at)])} />
          </div>
        )}

        {tab === "users" && (
          <Table testId="sa-users-table"
            cols={[t.colUser, t.colContact, t.colPlan, t.colRole, t.colAvatar, t.colStatus, t.colLegal, t.colCredits, t.colDate]}
            rows={data.users.map((u) => [
              `${u.name} (@${u.username})`, u.email || u.phone || "—", u.tier.toUpperCase(), u.role,
              u.avatar_name || "—", u.avatar_status, u.legal_accepted ? "✓" : "✗", u.credits_used, fmtDate(u.created_at),
            ])} />
        )}

        {tab === "matches" && (
          <Table testId="sa-matches-table"
            cols={[t.colPair, t.colCompat, t.colMatchStatus, t.colDate]}
            rows={data.matches.map((m) => [
              `${m.avatar_a} × ${m.avatar_b}`, m.compatibility != null ? `${m.compatibility}%` : "—", m.status, fmtDate(m.created_at),
            ])} />
        )}

        {tab === "legal" && (
          <Table testId="sa-consents-table"
            cols={[t.colUser, t.colVersion, t.colIp, t.colSignature, t.colDate]}
            rows={data.consents.map((c) => [c.user, `v${c.terms_version}`, c.ip_address, `${c.signature}…`, fmtDate(c.timestamp)])} />
        )}

        {tab === "security" && (
          <Table testId="sa-logs-table"
            cols={[t.colUser, t.colEvent, t.colStatus, t.colDate]}
            rows={data.logs.map((l) => [l.user, l.event, l.status, fmtDate(l.timestamp)])} />
        )}
      </main>
    </div>
  );
}
